import { get, isObject, isString } from 'radash';
import type { AgendaAttributeValue, AttributeType, JoinedRecordRef, UpdateWithNestedListReq, ViewParameter } from 'types';
import type {
  InlineRecordType,
  JoinedColumnSource,
  JoinedRowIdentity,
  UserAgendaTableDataSchema,
  UserAgendaTableDataSchemaType,
} from 'types/userAgendaTable';
import { VIEW_ITEM_STATE, type TABLE_CELL_TYPE } from 'types/enums';
import { getAttributeForSave, getCellTypeByFormType, NEW_RECORD_PREFIX } from 'utils/agendaRecord/agendaRecord';
import type { FlattenedJoinedAgendaRecord } from 'types/records';

type NestedListItem = NonNullable<UpdateWithNestedListReq['body']['nestedListData']>[number];

type SaveBucket = {
  agendaId: string;
  recordId: string;
  attributes: Map<string, AttributeType>;
  nestedListData: Map<string, NestedListItem>;
};

const joinedColumnFromParameter = (parameter: ViewParameter): JoinedColumnSource | undefined => {
  const nestingLevel = parameter.nestingLevel ?? 1;
  const formItemId = parameter.parentRecordsFormItemId;

  if (nestingLevel < 2 || !formItemId) return undefined;

  return {
    level: nestingLevel >= 3 ? 'secondLevel' : 'nested',
    formItemId,
    agendaId: parameter.sourceAgendaId ?? null,
  };
};

export const buildJoinedListingDataSchema = (viewParameters: ViewParameter[]): UserAgendaTableDataSchemaType =>
  viewParameters
    .filter(({ viewItemState }) => [VIEW_ITEM_STATE.ENABLED_AND_FILTER, VIEW_ITEM_STATE.ENABLED_AND_NO_FILTER].includes(viewItemState))
    .reduce<UserAgendaTableDataSchemaType>((acc, item, index) => {
      const parameterId = item.parameterId ?? '';
      const joinedColumn = joinedColumnFromParameter(item);

      acc[parameterId] = {
        label: item.parameterId,
        formItemDefinition: item.formItemDefinition,
        type: getCellTypeByFormType(item.formItemType!) as TABLE_CELL_TYPE,
        originalType: String(item.formItemType),
        customKey: String(item.parameterTitle),
        group: '',
        groupName: '',
        groupOrder: 0,
        dateMask: item.formItemDefinition?.date?.dateMask || '',
        // Shared by joined and plain filtered listings. Parent columns follow this flag too.
        isEditable: Boolean(item.isEditable),
        ...(joinedColumn ? { joinedColumn } : {}),
        showInToolbar: item.showInToolbar ?? false,
        order: index,
      };

      return acc;
    }, {});

const readJoinedRecordRef = (row: object | undefined, key: 'nestedRecord' | 'secondLevelNestedRecord'): JoinedRecordRef | null => {
  const value = get(row, key);
  if (!isObject(value) || !('id' in value) || !isString(value.id) || !value.id) return null;

  const agendaId = 'agendaId' in value && isString(value.agendaId) ? value.agendaId : '';
  return { id: value.id, agendaId };
};

export const joinedTableRowId = (parentId: string, nestedId?: string | null, secondLevelId?: string | null): string =>
  [parentId, nestedId, secondLevelId].filter((id): id is string => Boolean(id)).join('__');

export const canEditJoinedCell = (column: Pick<UserAgendaTableDataSchema, 'joinedColumn'> | undefined, row: object | undefined): boolean => {
  const source = column?.joinedColumn;
  if (!source) return true;

  return Boolean(readJoinedRecordRef(row, source.level === 'secondLevel' ? 'secondLevelNestedRecord' : 'nestedRecord')?.id);
};

export const joinedIdentityByRowId = (items?: FlattenedJoinedAgendaRecord[]): Map<string, JoinedRowIdentity> => {
  const identities = new Map<string, JoinedRowIdentity>();

  items?.forEach((item) => {
    identities.set(item.rowId || item.id, {
      parentRecordId: item.id,
      nestedRecord: item.nestedRecord,
      secondLevelNestedRecord: item.secondLevelNestedRecord,
    });
  });

  return identities;
};

export const getDirtyColumnIds = (dirtyRow: unknown): string[] => {
  if (!isObject(dirtyRow)) return [];

  return Object.entries(dirtyRow)
    .filter(([, dirty]) => Boolean(dirty))
    .map(([columnId]) => columnId);
};

export const getDirtyAttributesForRow = (
  dirtyRow: unknown,
  rowValues: Record<string, unknown> | undefined,
  dataSchema: UserAgendaTableDataSchemaType
): AttributeType[] => {
  if (!rowValues) return [];

  return getDirtyColumnIds(dirtyRow).flatMap((columnId) => {
    const column = dataSchema[columnId];
    const type = column?.formItemDefinition?.type;
    if (!type) return [];

    return [getAttributeForSave(type, columnId, rowValues[columnId] as AgendaAttributeValue, column.formItemDefinition)];
  });
};

export const emptyIdentity = (): JoinedRowIdentity => ({ nestedRecord: null, secondLevelNestedRecord: null });

export type DroppedJoinedAttribute = {
  rowId: string;
  formItemId: string;
  reason: 'missingRecord' | 'missingSecondLevelParent' | 'addedRow';
};

export class JoinedValueConflictError extends Error {
  constructor(
    readonly recordId: string,
    readonly formItemId: string
  ) {
    super(`Conflicting values for ${formItemId} on record ${recordId}`);
    this.name = 'JoinedValueConflictError';
  }
}

const sameAttribute = (left: AttributeType, right: AttributeType) => JSON.stringify(left) === JSON.stringify(right);

const partitionJoinedAttributes = (attributes: AttributeType[], dataSchema: UserAgendaTableDataSchemaType) => {
  const parentAttributes: AttributeType[] = [];
  const nestedAttributes: AttributeType[] = [];
  const secondLevelAttributes: AttributeType[] = [];

  attributes.forEach((attribute) => {
    const level = dataSchema[attribute.formItemId]?.joinedColumn?.level;

    if (level === 'secondLevel') secondLevelAttributes.push(attribute);
    else if (level === 'nested') nestedAttributes.push(attribute);
    else parentAttributes.push(attribute);
  });

  return { parentAttributes, nestedAttributes, secondLevelAttributes };
};

const getSaveBucket = (buckets: Map<string, SaveBucket>, agendaId: string, recordId: string): SaveBucket => {
  const key = `${agendaId}:${recordId}`;
  const existing = buckets.get(key);

  if (existing) return existing;

  const bucket: SaveBucket = { agendaId, recordId, attributes: new Map(), nestedListData: new Map() };
  buckets.set(key, bucket);

  return bucket;
};

const addAttributes = (bucket: SaveBucket, attributes: AttributeType[]) => {
  attributes.forEach((attribute) => {
    const existing = bucket.attributes.get(attribute.formItemId);

    if (existing && !sameAttribute(existing, attribute)) {
      throw new JoinedValueConflictError(bucket.recordId, attribute.formItemId);
    }

    bucket.attributes.set(attribute.formItemId, attribute);
  });
};

const addNestedListData = (bucket: Map<string, NestedListItem>, items: NestedListItem[]) => {
  items.forEach((item) => {
    const key = `${item.formItemId}:${item.recordId ?? ''}`;
    const existing = bucket.get(key);

    if (!existing) {
      bucket.set(key, { ...item, attributes: [...item.attributes] });
      return;
    }

    item.attributes.forEach((attribute) => {
      const index = existing.attributes.findIndex((current) => current.formItemId === attribute.formItemId);

      if (index >= 0) {
        if (!sameAttribute(existing.attributes[index], attribute)) {
          throw new JoinedValueConflictError(item.recordId || existing.recordId || '', attribute.formItemId);
        }

        return;
      }

      existing.attributes.push(attribute);
    });
  });
};

const toSaveRequest = (bucket: SaveBucket): UpdateWithNestedListReq | null => {
  const attributes = Array.from(bucket.attributes.values());
  const nestedListData = Array.from(bucket.nestedListData.values());

  if (!attributes.length && !nestedListData.length) return null;

  return {
    agendaId: bucket.agendaId,
    body: {
      recordId: bucket.recordId,
      attributes,
      ...(nestedListData.length ? { nestedListData } : {}),
    },
  };
};

export const splitJoinedAttributes = (
  attributes: AttributeType[],
  dataSchema: UserAgendaTableDataSchemaType,
  identity: JoinedRowIdentity,
  droppedAttributes?: DroppedJoinedAttribute[],
  rowId = ''
): { parentAttributes: AttributeType[]; nestedListData: UpdateWithNestedListReq['body']['nestedListData'] } => {
  const parentAttributes: AttributeType[] = [];
  const nestedGroups = new Map<string, NonNullable<UpdateWithNestedListReq['body']['nestedListData']>[number]>();

  attributes.forEach((attribute) => {
    const source = dataSchema[attribute.formItemId]?.joinedColumn;
    if (!source) {
      parentAttributes.push(attribute);
      return;
    }

    const record = source.level === 'secondLevel' ? identity.secondLevelNestedRecord : identity.nestedRecord;
    if (!record?.id) {
      droppedAttributes?.push({ rowId, formItemId: attribute.formItemId, reason: 'missingRecord' });
      return;
    }

    const key = `${source.level}:${source.formItemId}:${record.id}`;
    const existing = nestedGroups.get(key);
    if (existing) {
      existing.attributes.push(attribute);
      return;
    }

    nestedGroups.set(key, {
      agendaId: record.agendaId || source.agendaId || '',
      formItemId: source.formItemId,
      recordId: record.id,
      attributes: [attribute],
    });
  });

  const nestedListData = Array.from(nestedGroups.values());
  return { parentAttributes, nestedListData: nestedListData.length ? nestedListData : undefined };
};

export const buildJoinedSaveRequests = (
  parentAgendaId: string,
  dirtyFields: object,
  formData: Record<string, Record<string, unknown> | undefined>,
  dataSchema: UserAgendaTableDataSchemaType,
  identityByRowId: Map<string, JoinedRowIdentity>,
  droppedAttributes: DroppedJoinedAttribute[] = []
): UpdateWithNestedListReq[] => {
  const rows = Object.entries(dirtyFields).flatMap(([rowId, dirtyRow]) => {
    if (rowId.startsWith(NEW_RECORD_PREFIX)) {
      getDirtyColumnIds(dirtyRow).forEach((formItemId) => {
        droppedAttributes.push({ rowId, formItemId, reason: 'addedRow' });
      });
      return [];
    }

    const attributes = getDirtyAttributesForRow(dirtyRow, formData[rowId], dataSchema);
    const identity = identityByRowId.get(rowId) ?? emptyIdentity();
    const partitioned = partitionJoinedAttributes(attributes, dataSchema);

    if (!partitioned.parentAttributes.length && !partitioned.nestedAttributes.length && !partitioned.secondLevelAttributes.length) return [];

    return [{ rowId, identity, ...partitioned }];
  });

  // A second-level row is linked from the first nested record, so that record is the parent of its own update.
  const secondLevelParentIds = new Set(
    rows.flatMap((row) => {
      const nestedRecord = row.identity.nestedRecord;
      return row.secondLevelAttributes.length && nestedRecord?.id && nestedRecord.agendaId ? [nestedRecord.id] : [];
    })
  );

  const parentBuckets = new Map<string, SaveBucket>();
  const nestedBuckets = new Map<string, SaveBucket>();

  rows.forEach((row) => {
    const parentRecordId = row.identity.parentRecordId || row.rowId;
    const parentBucket = getSaveBucket(parentBuckets, parentAgendaId, parentRecordId);
    addAttributes(parentBucket, row.parentAttributes);

    const nestedRecord = row.identity.nestedRecord;

    if (nestedRecord?.id && nestedRecord.agendaId && secondLevelParentIds.has(nestedRecord.id)) {
      const nestedBucket = getSaveBucket(nestedBuckets, nestedRecord.agendaId, nestedRecord.id);
      addAttributes(nestedBucket, row.nestedAttributes);
      const { nestedListData } = splitJoinedAttributes(row.secondLevelAttributes, dataSchema, row.identity, droppedAttributes, row.rowId);
      addNestedListData(nestedBucket.nestedListData, nestedListData ?? []);
      return;
    }

    row.secondLevelAttributes.forEach((attribute) => {
      droppedAttributes.push({ rowId: row.rowId, formItemId: attribute.formItemId, reason: 'missingSecondLevelParent' });
    });

    const { nestedListData } = splitJoinedAttributes(row.nestedAttributes, dataSchema, row.identity, droppedAttributes, row.rowId);
    addNestedListData(parentBucket.nestedListData, nestedListData ?? []);
  });

  if (droppedAttributes.length) {
    // eslint-disable-next-line no-console
    console.warn('Joined save skipped attributes with no target record', droppedAttributes);
  }

  return [...parentBuckets.values(), ...nestedBuckets.values()].flatMap((bucket) => {
    const request = toSaveRequest(bucket);

    return request ? [request] : [];
  });
};

export const joinedParentCellPatches = (
  rowId: string,
  columnId: string,
  dataSchema: UserAgendaTableDataSchemaType,
  identityByRowId: Map<string, JoinedRowIdentity>
): string[] => {
  if (dataSchema[columnId]?.joinedColumn) return [];

  const parentRecordId = identityByRowId.get(rowId)?.parentRecordId;
  if (!parentRecordId) return [];

  return [...identityByRowId.entries()].flatMap(([otherRowId, identity]) =>
    otherRowId !== rowId && identity.parentRecordId === parentRecordId ? [otherRowId] : []
  );
};

export const splitJoinedCalculatePayload = (
  attributes: AttributeType[],
  dataSchema: UserAgendaTableDataSchemaType,
  identity: JoinedRowIdentity
): { attributes: AttributeType[]; inlineRecordData: InlineRecordType[] } => {
  const { parentAttributes, nestedListData } = splitJoinedAttributes(attributes, dataSchema, identity);

  return {
    attributes: parentAttributes,
    inlineRecordData: (nestedListData ?? []).flatMap((item) =>
      item.recordId ? [{ agendaId: item.agendaId, recordId: item.recordId, attributes: item.attributes }] : []
    ),
  };
};
